import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Trophy, BarChart3, TrendingUp, UserCheck } from 'lucide-react';
import { apiClient } from '../../api/client';
import { ASHARE_BILINGUAL } from '../../shared/constants/bilingualTitles';

export interface ChipBin {
  bin_index: number;
  price_low: string;
  price_high: string;
  price_center: string;
  density: string;
  density_pct: string;
}

export interface ChipDistributionResult {
  symbol: string;
  bins_count: number;
  min_price: string;
  max_price: string;
  current_price: string;
  cost_center: string;
  cost_diff_pct: string;
  profit_ratio: string;
  trapped_ratio: string;
  at_price_ratio: string;
  concentration_70_low: string;
  concentration_70_high: string;
  concentration_70_ratio: string;
  concentration_90_low: string;
  concentration_90_high: string;
  concentration_90_ratio: string;
  peak_morphology: 'SINGLE_PEAK_DENSE' | 'DOUBLE_PEAK_CANYON' | 'DIVERGENT' | string;
  bins: ChipBin[];
}

export interface AccumulationDiagnosis {
  control_score: string;
  accumulation_stage: 'ACCUMULATION' | 'WASHOUT' | 'MARKUP' | 'DISTRIBUTION' | 'NEUTRAL' | string;
  stage_description: string;
  chip_tightness_score: string;
  support_proximity_score: string;
  volume_price_harmony_score: string;
  volatility_contraction_score: string;
  avg_turnover_90d: string;
  total_turnover_90d: string;
}

export interface MainForceProfileResponse {
  status: 'fresh' | 'stale' | 'empty' | 'disconnected' | 'loading' | string;
  symbol: string;
  name: string;
  trade_date: string;
  total_bars_evaluated: number;
  chip_distribution: ChipDistributionResult;
  diagnosis: AccumulationDiagnosis;
  meta?: {
    state: string;
    scan_completed_at?: string;
    benchmark_code?: string;
    stale_reason?: string | null;
  };
}

export interface ScreenerItem {
  thscode: string;
  symbol: string;
  name: string;
  control_score: string;
  accumulation_stage: string;
  stage_description: string;
  cost_center: string;
  current_price: string;
  cost_diff_pct: string;
  profit_ratio: string;
  trapped_ratio: string;
  concentration_70_ratio: string;
  peak_morphology: string;
}

export interface MainForceScreenerResponse {
  status: string;
  trade_date: string;
  total_items: number;
  items: ScreenerItem[];
  meta?: {
    state: string;
    scan_completed_at?: string;
    benchmark_code?: string;
    stale_reason?: string | null;
  };
}

const PRESET_SYMBOLS = [
  { symbol: '000001', name: '平安银行' },
  { symbol: '600519', name: '贵州茅台' },
  { symbol: '300750', name: '宁德时代' },
  { symbol: '002594', name: '比亚迪' },
  { symbol: '601318', name: '中国平安' },
  { symbol: '002028', name: '思源电气' },
  { symbol: '600036', name: '招商银行' },
];

export const AShareMainProfileView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'profile' | 'screener'>('profile');
  const [selectedSymbol, setSelectedSymbol] = useState<string>('000001');
  const [inputSymbol, setInputSymbol] = useState<string>('000001');

  // Single Profile State
  const [profileData, setProfileData] = useState<MainForceProfileResponse | null>(null);
  const [profileLoading, setProfileLoading] = useState<boolean>(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Screener State
  const [screenerData, setScreenerData] = useState<MainForceScreenerResponse | null>(null);
  const [screenerLoading, setScreenerLoading] = useState<boolean>(false);
  const [minScoreFilter, setMinScoreFilter] = useState<string>('60.00');
  const [stageFilter, setStageFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('control_score');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch single profile
  const fetchProfile = useCallback(async (sym: string) => {
    setProfileLoading(true);
    setProfileError(null);
    try {
      const res = await apiClient.request<MainForceProfileResponse>(
        `/api/v1/market/a-share/main-profile/${sym}`,
        { params: { limit: 90 } }
      );
      setProfileData(res);
    } catch (err: any) {
      setProfileError(err.message || '加载主力画像失败');
    } finally {
      setProfileLoading(false);
    }
  }, []);

  // Fetch screener pool
  const fetchScreener = useCallback(async () => {
    setScreenerLoading(true);
    try {
      const params: Record<string, string | number> = {
        min_score: minScoreFilter,
        sort_by: sortBy,
        order: sortOrder,
        limit: 30,
      };
      if (stageFilter) {
        params.stage = stageFilter;
      }
      const res = await apiClient.request<MainForceScreenerResponse>(
        '/api/v1/market/a-share/main-profile/screener',
        { params }
      );
      setScreenerData(res);
    } catch (err: any) {
      // Keep existing data or empty
    } finally {
      setScreenerLoading(false);
    }
  }, [minScoreFilter, stageFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchProfile(selectedSymbol);
  }, [selectedSymbol, fetchProfile]);

  useEffect(() => {
    if (activeTab === 'screener') {
      fetchScreener();
    }
  }, [activeTab, fetchScreener]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const clean = inputSymbol.trim().toUpperCase();
    if (clean) {
      setSelectedSymbol(clean);
      setActiveTab('profile');
    }
  };

  const getStageBadgeStyle = (stage: string) => {
    switch (stage) {
      case 'ACCUMULATION':
        return { bg: 'var(--badge-bullish-bg)', text: 'var(--color-bullish)', border: 'var(--color-bullish)', label: '底背离温和吸筹' };
      case 'WASHOUT':
        return { bg: 'var(--badge-info-bg)', text: 'var(--accent-cyan)', border: 'var(--accent-cyan)', label: '缩量洗盘震荡' };
      case 'MARKUP':
        return { bg: 'var(--badge-bearish-bg)', text: 'var(--color-bearish)', border: 'var(--color-bearish)', label: '脱离成本主升' };
      case 'DISTRIBUTION':
        return { bg: 'var(--badge-warning-bg)', text: 'var(--color-warning)', border: 'var(--color-warning)', label: '高位派发出货' };
      default:
        return { bg: 'var(--badge-neutral-bg, var(--bg-secondary))', text: 'var(--text-secondary)', border: 'var(--border-subtle)', label: '常规震荡无控盘' };
    }
  };

  const getPeakMorphologyBadge = (morph: string) => {
    switch (morph) {
      case 'SINGLE_PEAK_DENSE':
        return { label: '单峰密集 (筹码高度锁定)', color: 'var(--color-bullish)' };
      case 'DOUBLE_PEAK_CANYON':
        return { label: '双峰峡谷 (多空分水岭)', color: 'var(--accent-cyan)' };
      default:
        return { label: '筹码发散 (分散无聚集)', color: 'var(--text-muted)' };
    }
  };

  const maxBinDensity = useMemo(() => {
    if (!profileData?.chip_distribution?.bins?.length) return 0.05;
    return Math.max(
      ...profileData.chip_distribution.bins.map((b) => parseFloat(b.density) || 0),
      0.02
    );
  }, [profileData]);

  return (
    <div className="view-container animate-fade-in" style={{ width: '100%', boxSizing: 'border-box' }}>
      {/* 1. Standardized TSO 2.0 View Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <UserCheck size={18} style={{ color: 'var(--accent-cyan)' }} />
            <h2 className="bilingual-header-title-zh">{ASHARE_BILINGUAL.mainProfile.zh}</h2>
          </div>
          <span className="bilingual-header-subtitle-en" title="MAIN FORCE & CHIP DENSITY PROFILE">{ASHARE_BILINGUAL.mainProfile.en}</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            换手衰减筹码密集峰直方图 · 主力吸筹体检诊断室 · 控盘度打分与持仓成本中枢
          </p>
        </div>

        <div className="view-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            borderRadius: '6px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-card)',
            fontSize: '12px',
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-muted)',
          }}>
            <span style={{ color: profileData?.status === 'fresh' ? 'var(--color-bullish)' : 'var(--color-warning)' }}>
              ● {profileData?.status?.toUpperCase() || 'LOADING'}
            </span>
            <span>|</span>
            <span>基准: 000300.SH</span>
          </div>
        </div>
      </div>

      {/* Symbol Search & Preset Quick Picker */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
        padding: '14px 18px',
        borderRadius: '8px',
        background: 'var(--bg-card)',
        border: '1px solid var(--border-card)',
        marginBottom: '20px',
      }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>标的代码:</span>
          <input
            type="text"
            value={inputSymbol}
            onChange={(e) => setInputSymbol(e.target.value)}
            placeholder="例如 000001"
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid var(--border-card)',
              background: 'var(--bg-card)',
              color: 'var(--text-primary)',
              fontSize: '13px',
              fontFamily: 'var(--font-mono)',
              width: '120px',
            }}
          />
          <button
            type="submit"
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              background: 'var(--accent-green, var(--color-bullish))',
              color: 'var(--text-inverse)',
              border: 'none',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            诊断分析
          </button>
        </form>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>快捷关注:</span>
          {PRESET_SYMBOLS.map((item) => (
            <button
              key={item.symbol}
              onClick={() => {
                setSelectedSymbol(item.symbol);
                setInputSymbol(item.symbol);
                setActiveTab('profile');
              }}
              style={{
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: '12px',
                fontFamily: 'var(--font-mono)',
                cursor: 'pointer',
                background: selectedSymbol === item.symbol ? 'var(--accent-green, var(--color-bullish))' : 'var(--bg-base)',
                color: selectedSymbol === item.symbol ? 'var(--text-inverse)' : 'var(--text-primary)',
                border: selectedSymbol === item.symbol ? '1px solid var(--accent-green, var(--color-bullish))' : '1px solid var(--border-card)',
              }}
            >
              {item.symbol} {item.name}
            </button>
          ))}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', borderBottom: '1px solid var(--border-card)', paddingBottom: '8px' }}>
        <button
          onClick={() => setActiveTab('profile')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            background: activeTab === 'profile' ? 'var(--bg-card)' : 'transparent',
            color: activeTab === 'profile' ? 'var(--accent-green, var(--color-bullish))' : 'var(--text-secondary)',
            border: activeTab === 'profile' ? '1px solid var(--border-card)' : 'none',
            borderBottom: activeTab === 'profile' ? '2px solid var(--accent-green, var(--color-bullish))' : 'none',
          }}
        >
          <Search size={14} /> 个股 90 日筹码峰深度体检
        </button>
        <button
          onClick={() => setActiveTab('screener')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            background: activeTab === 'screener' ? 'var(--bg-card)' : 'transparent',
            color: activeTab === 'screener' ? 'var(--accent-green, var(--color-bullish))' : 'var(--text-secondary)',
            border: activeTab === 'screener' ? '1px solid var(--border-card)' : 'none',
            borderBottom: activeTab === 'screener' ? '2px solid var(--accent-green, var(--color-bullish))' : 'none',
          }}
        >
          <Trophy size={14} /> 全市场高控盘吸筹池
        </button>
      </div>

      {/* TAB 1: Single Stock Profile & Diagnosis */}
      {activeTab === 'profile' && (
        <div>
          {profileLoading && (
            <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-muted)' }}>
              正在执行 90 日日线换手衰减模型与筹码峰分布推导...
            </div>
          )}

          {profileError && (
            <div style={{ padding: '20px', borderRadius: '8px', background: 'var(--badge-bearish-bg)', color: 'var(--color-bearish)', border: '1px solid var(--color-bearish)', marginBottom: '20px' }}>
              {profileError}
            </div>
          )}

          {!profileLoading && profileData && profileData.status === 'empty' && (
            <div style={{ padding: '40px', textAlign: 'center', background: 'var(--bg-card)', borderRadius: '8px', border: '1px solid var(--border-card)', color: 'var(--text-muted)' }}>
              暂未收录标的 {selectedSymbol} 的 90 日历史 K 线数据，请确认代码是否正确。
            </div>
          )}

          {!profileLoading && profileData && profileData.status !== 'empty' && (
            <div>
              {/* Diagnosis Banner & Control Score Gauge */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: '16px',
                marginBottom: '20px',
              }}>
                {/* Score & Stage Card */}
                <div style={{
                  padding: '20px',
                  borderRadius: '8px',
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-card)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                }}>
                  <div style={{
                    width: '90px',
                    height: '90px',
                    borderRadius: '50%',
                    border: '4px solid var(--accent-green, var(--color-bullish))',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--badge-bullish-bg)',
                  }}>
                    <span style={{ fontSize: '24px', fontWeight: 800, fontFamily: 'var(--font-mono)', color: 'var(--color-bullish)' }}>
                      {profileData.diagnosis.control_score}
                    </span>
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>控盘度评分</span>
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>
                        {profileData.symbol} {profileData.name}
                      </span>
                      {(() => {
                        const b = getStageBadgeStyle(profileData.diagnosis.accumulation_stage);
                        return (
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: b.bg,
                            color: b.text,
                            border: `1px solid ${b.border}`,
                          }}>
                            {b.label}
                          </span>
                        );
                      })()}
                    </div>
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      {profileData.diagnosis.stage_description}
                    </p>
                    <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                      形态判定: {getPeakMorphologyBadge(profileData.chip_distribution.peak_morphology).label}
                    </div>
                  </div>
                </div>

                {/* 4 Pillars Breakdown */}
                <div style={{
                  padding: '20px',
                  borderRadius: '8px',
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-card)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>
                    <BarChart3 size={15} style={{ color: 'var(--accent-cyan)' }} /> 四维吸筹诊断细项 (Pillars Breakdown)
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '2px' }}>
                        <span>筹码集中度 (Tightness)</span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{profileData.diagnosis.chip_tightness_score} / 30</span>
                      </div>
                      <div style={{ height: '6px', borderRadius: '3px', background: 'var(--border-subtle)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(parseFloat(profileData.diagnosis.chip_tightness_score) / 30) * 100}%`, background: 'var(--accent-green, var(--color-bullish))' }} />
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '2px' }}>
                        <span>成本支撑度 (Support Proximity)</span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{profileData.diagnosis.support_proximity_score} / 25</span>
                      </div>
                      <div style={{ height: '6px', borderRadius: '3px', background: 'var(--border-subtle)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(parseFloat(profileData.diagnosis.support_proximity_score) / 25) * 100}%`, background: 'var(--accent-cyan)' }} />
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '2px' }}>
                        <span>量价配合度 (Volume Harmony)</span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{profileData.diagnosis.volume_price_harmony_score} / 25</span>
                      </div>
                      <div style={{ height: '6px', borderRadius: '3px', background: 'var(--border-subtle)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(parseFloat(profileData.diagnosis.volume_price_harmony_score) / 25) * 100}%`, background: 'var(--color-warning)' }} />
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '2px' }}>
                        <span>震荡收敛度 (Contraction)</span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{profileData.diagnosis.volatility_contraction_score} / 20</span>
                      </div>
                      <div style={{ height: '6px', borderRadius: '3px', background: 'var(--border-subtle)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(parseFloat(profileData.diagnosis.volatility_contraction_score) / 20) * 100}%`, background: 'var(--accent-purple, var(--accent-primary))' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Quantitative Metrics Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '12px',
                marginBottom: '20px',
              }}>
                <div style={{ padding: '12px 16px', borderRadius: '6px', background: 'var(--bg-card)', border: '1px solid var(--border-card)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>主力成本中枢 (Cost)</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', marginTop: '2px' }}>
                    ¥{profileData.chip_distribution.cost_center}
                  </div>
                </div>

                <div style={{ padding: '12px 16px', borderRadius: '6px', background: 'var(--bg-card)', border: '1px solid var(--border-card)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>现价偏离成本 (Diff)</div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    fontFamily: 'var(--font-mono)',
                    marginTop: '2px',
                    color: parseFloat(profileData.chip_distribution.cost_diff_pct) >= 0 ? 'var(--color-bearish)' : 'var(--color-bullish)',
                  }}>
                    {parseFloat(profileData.chip_distribution.cost_diff_pct) >= 0 ? '+' : ''}{profileData.chip_distribution.cost_diff_pct}%
                  </div>
                </div>

                <div style={{ padding: '12px 16px', borderRadius: '6px', background: 'var(--bg-card)', border: '1px solid var(--border-card)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>获利盘比例 (Profit)</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--color-bullish)', marginTop: '2px' }}>
                    {(parseFloat(profileData.chip_distribution.profit_ratio) * 100).toFixed(1)}%
                  </div>
                </div>

                <div style={{ padding: '12px 16px', borderRadius: '6px', background: 'var(--bg-card)', border: '1px solid var(--border-card)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>被套盘比例 (Trapped)</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--color-bearish)', marginTop: '2px' }}>
                    {(parseFloat(profileData.chip_distribution.trapped_ratio) * 100).toFixed(1)}%
                  </div>
                </div>

                <div style={{ padding: '12px 16px', borderRadius: '6px', background: 'var(--bg-card)', border: '1px solid var(--border-card)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>70% 筹码集中度</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent-cyan)', marginTop: '2px' }}>
                    {(parseFloat(profileData.chip_distribution.concentration_70_ratio) * 100).toFixed(1)}%
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    [¥{profileData.chip_distribution.concentration_70_low} ~ ¥{profileData.chip_distribution.concentration_70_high}]
                  </div>
                </div>
              </div>

              {/* Volume Profile at Price Histogram (50 Price Bins) */}
              <div style={{
                padding: '24px',
                borderRadius: '8px',
                background: 'var(--bg-card)',
                border: '1px solid var(--border-card)',
                marginBottom: '20px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div>
                    <div className="bilingual-card-title" style={{ marginBottom: '4px' }}>
                      <h3 className="bilingual-card-title-zh" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <TrendingUp size={16} style={{ color: 'var(--accent-cyan)' }} /> 筹码密集峰分布直方图
                      </h3>
                      <span className="bilingual-card-subtitle-en">VOLUME PROFILE AT PRICE</span>
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      50 档离散价格箱 · 绿色为获利盘支撑 · 红色为上方套牢盘压力
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '11px', fontFamily: 'var(--font-mono)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '12px', height: '12px', background: 'var(--color-bullish)', borderRadius: '2px' }} />
                      <span>获利盘 (低于现价)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '12px', height: '12px', background: 'var(--color-bearish)', borderRadius: '2px' }} />
                      <span>套牢盘 (高于现价)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '12px', height: '2px', background: 'var(--accent-cyan)' }} />
                      <span>主力成本中枢线</span>
                    </div>
                  </div>
                </div>

                {/* Histogram Bars Container */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', maxHeight: '520px', overflowY: 'auto', paddingRight: '8px' }}>
                  {profileData.chip_distribution.bins.slice().reverse().map((bin) => {
                    const centerP = parseFloat(bin.price_center);
                    const currP = parseFloat(profileData.chip_distribution.current_price);
                    const costP = parseFloat(profileData.chip_distribution.cost_center);
                    const densityVal = parseFloat(bin.density) || 0;
                    const barWidthPct = Math.min(100, (densityVal / maxBinDensity) * 100);

                    const isProfit = centerP < currP;
                    const isAtPrice = Math.abs(centerP - currP) <= (parseFloat(bin.price_high) - parseFloat(bin.price_low));
                    const isNearCost = Math.abs(centerP - costP) <= (parseFloat(bin.price_high) - parseFloat(bin.price_low));

                    return (
                      <div
                        key={bin.bin_index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '11px',
                          fontFamily: 'var(--font-mono)',
                          padding: '2px 4px',
                          background: isAtPrice ? 'var(--badge-warning-bg)' : 'transparent',
                          borderRadius: '2px',
                        }}
                      >
                        <div style={{ width: '55px', textAlign: 'right', color: 'var(--text-secondary)' }}>
                          ¥{bin.price_center}
                        </div>

                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', position: 'relative', height: '12px' }}>
                          <div
                            style={{
                              height: '8px',
                              width: `${barWidthPct}%`,
                              background: isProfit ? 'var(--color-bullish)' : 'var(--color-bearish)',
                              borderRadius: '2px',
                              transition: 'width 0.2s ease',
                            }}
                          />
                          {isNearCost && (
                            <div
                              style={{
                                position: 'absolute',
                                left: `${barWidthPct + 1}%`,
                                height: '10px',
                                width: '3px',
                                background: 'var(--accent-cyan)',
                              }}
                              title="主力成本中枢"
                            />
                          )}
                        </div>

                        <div style={{ width: '50px', textAlign: 'right', color: 'var(--text-muted)' }}>
                          {bin.density_pct}
                        </div>

                        <div style={{ width: '45px', textAlign: 'left' }}>
                          {isAtPrice && (
                            <span style={{ fontSize: '10px', color: 'var(--color-warning)', fontWeight: 700 }}>◀ 现价</span>
                          )}
                          {isNearCost && !isAtPrice && (
                            <span style={{ fontSize: '10px', color: 'var(--accent-cyan)', fontWeight: 600 }}>◀ 成本</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: All-Market High-Control Screener Table */}
      {activeTab === 'screener' && (
        <div style={{
          padding: '24px',
          borderRadius: '8px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-card)',
        }}>
          {/* Filter Toolbar */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600 }}>最低控盘分:</span>
              <select
                value={minScoreFilter}
                onChange={(e) => setMinScoreFilter(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--border-card)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '12px' }}
              >
                <option value="50.00">≥ 50 (常规及以上)</option>
                <option value="60.00">≥ 60 (主力活跃)</option>
                <option value="70.00">≥ 70 (高控盘吸筹)</option>
                <option value="80.00">≥ 80 (极致锁仓)</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600 }}>阶段过滤:</span>
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--border-card)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '12px' }}
              >
                <option value="">全阶段展示</option>
                <option value="ACCUMULATION">ACCUMULATION (吸筹期)</option>
                <option value="WASHOUT">WASHOUT (洗盘期)</option>
                <option value="MARKUP">MARKUP (主升期)</option>
                <option value="DISTRIBUTION">DISTRIBUTION (派发期)</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600 }}>排序维度:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--border-card)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '12px' }}
              >
                <option value="control_score">控盘度评分</option>
                <option value="concentration_70">70% 筹码集中度</option>
                <option value="profit_ratio">获利盘比例</option>
                <option value="cost_diff_pct">偏离成本比例</option>
              </select>
              <button
                type="button"
                onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                style={{
                  padding: '6px 10px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-card)',
                  fontSize: '12px',
                  background: 'var(--bg-base)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                }}
                title="切换升降序"
              >
                {sortOrder === 'desc' ? '降序 ↓' : '升序 ↑'}
              </button>
            </div>

            <button
              onClick={() => fetchScreener()}
              style={{
                padding: '6px 14px',
                borderRadius: '6px',
                background: 'var(--accent-green, var(--color-bullish))',
                color: 'var(--text-inverse)',
                border: 'none',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              刷新筛选
            </button>
          </div>

          {screenerLoading && (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
              正在检索全市场高控盘吸筹候选股票池...
            </div>
          )}

          {!screenerLoading && screenerData && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-card)', color: 'var(--text-muted)' }}>
                    <th style={{ padding: '10px' }}>代码/名称</th>
                    <th style={{ padding: '10px' }}>控盘分</th>
                    <th style={{ padding: '10px' }}>当前阶段</th>
                    <th style={{ padding: '10px' }}>成本中枢</th>
                    <th style={{ padding: '10px' }}>现价</th>
                    <th style={{ padding: '10px' }}>偏离度</th>
                    <th style={{ padding: '10px' }}>获利盘</th>
                    <th style={{ padding: '10px' }}>70%集中度</th>
                    <th style={{ padding: '10px' }}>形态判定</th>
                    <th style={{ padding: '10px' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {screenerData.items.map((item) => {
                    const b = getStageBadgeStyle(item.accumulation_stage);
                    return (
                      <tr
                        key={item.thscode}
                        style={{ borderBottom: '1px solid var(--border-card)' }}
                      >
                        <td style={{ padding: '10px', fontWeight: 600 }}>
                          <span style={{ fontFamily: 'var(--font-mono)' }}>{item.symbol}</span>
                          <span style={{ marginLeft: '6px', color: 'var(--text-secondary)' }}>{item.name}</span>
                        </td>
                        <td style={{ padding: '10px', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--color-bullish)' }}>
                          {item.control_score}
                        </td>
                        <td style={{ padding: '10px' }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: b.bg,
                            color: b.text,
                            border: `1px solid ${b.border}`,
                          }}>
                            {b.label}
                          </span>
                        </td>
                        <td style={{ padding: '10px', fontFamily: 'var(--font-mono)' }}>
                          ¥{item.cost_center}
                        </td>
                        <td style={{ padding: '10px', fontFamily: 'var(--font-mono)' }}>
                          ¥{item.current_price}
                        </td>
                        <td style={{
                          padding: '10px',
                          fontFamily: 'var(--font-mono)',
                          fontWeight: 600,
                          color: parseFloat(item.cost_diff_pct) >= 0 ? 'var(--color-bearish)' : 'var(--color-bullish)',
                        }}>
                          {parseFloat(item.cost_diff_pct) >= 0 ? '+' : ''}{item.cost_diff_pct}%
                        </td>
                        <td style={{ padding: '10px', fontFamily: 'var(--font-mono)', color: 'var(--color-bullish)' }}>
                          {(parseFloat(item.profit_ratio) * 100).toFixed(1)}%
                        </td>
                        <td style={{ padding: '10px', fontFamily: 'var(--font-mono)', color: 'var(--accent-cyan)' }}>
                          {(parseFloat(item.concentration_70_ratio) * 100).toFixed(1)}%
                        </td>
                        <td style={{ padding: '10px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                          {getPeakMorphologyBadge(item.peak_morphology).label.split(' ')[0]}
                        </td>
                        <td style={{ padding: '10px' }}>
                          <button
                            onClick={() => {
                              setSelectedSymbol(item.symbol);
                              setInputSymbol(item.symbol);
                              setActiveTab('profile');
                            }}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              background: 'var(--bg-base)',
                              border: '1px solid var(--border-card)',
                              fontSize: '11px',
                              cursor: 'pointer',
                              color: 'var(--accent-green, var(--color-bullish))',
                              fontWeight: 600,
                            }}
                          >
                            查看画像 ➔
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
